import { marked } from 'marked';
import type { ContractSection, CustomVariable, DiagnosticCount } from './types';

// ── Variable grouping (client-side) ──────────────────────────────────────────

export const VARIABLE_GROUPS: { label: string; color: string; keys: string[] }[] = [
  {
    label: 'Cliente',
    color: '#4f98a3',
    keys: ['cliente_nome', 'cliente_indirizzo', 'cliente_citta', 'cliente_email', 'cliente_data_nascita', 'cliente_luogo_nascita', 'cliente_cellulare'],
  },
  {
    label: 'Evento',
    color: '#1a5f6f',
    keys: ['evento_data', 'evento_location', 'evento_tipo', 'evento_festeggiato'],
  },
  {
    label: 'Pagamenti',
    color: '#e8af34',
    keys: ['totale', 'acconto1', 'acconto2', 'saldo'],
  },
  {
    label: 'Studio',
    color: '#4f98a3',
    keys: ['fotografo_nome', 'studio_nome', 'studio_indirizzo', 'studio_piva', 'studio_citta'],
  },
  {
    label: 'Servizi',
    color: '#e8af34',
    keys: ['tabella_servizi'],
  },
  {
    label: 'Sistema',
    color: '#797876',
    keys: ['data_contratto', 'firma_cliente', 'firma_fotografo'],
  },
];

export function getGroupForKey(key: string): typeof VARIABLE_GROUPS[0] | null {
  return VARIABLE_GROUPS.find(g => g.keys.includes(key)) ?? null;
}

// ── Placeholder map for live preview ────────────────────────────────────────

export const PLACEHOLDER_MAP: Record<string, string> = {
  cliente_nome:       'Marco Rossi',
  cliente_indirizzo:  'Via Roma 1',
  cliente_citta:      'Milano',
  cliente_email:      'marco.rossi@esempio.it',
  cliente_data_nascita: '15/05/1990',
  cliente_luogo_nascita: 'Milano',
  cliente_cellulare: '+39 333 1234567',
  evento_data:        'sabato 14 giugno 2025',
  evento_location:    'Villa Borghese, Roma',
  evento_tipo:        '{{evento_tipo}}',
  evento_festeggiato: 'Marco e Giulia',
  totale:             '€ 2.500,00',
  acconto1:           '€ 500,00',
  acconto2:           '—',
  saldo:              '€ 2.000,00',
  fotografo_nome:     'Marco Rossi',
  studio_nome:        'Studio Rossi Foto',
  studio_indirizzo:   'Via Esempio 10',
  studio_piva:        'IT12345678901',
  studio_citta:       'Roma',
  data_contratto:     '01/05/2025',
  firma_cliente:      '_____________________ — Data: ___/___/_____',
  firma_fotografo:    '_____________________ — Data: ___/___/_____',
  tabella_servizi:    '<table style="width:100%;border-collapse:collapse;margin:20px 0;background:#fff;border-radius:6px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.05)"><thead><tr><th style="background:#f5f5f3;padding:10px 12px;text-align:left;font-size:10.5pt;font-weight:600;color:#1a5f6f;border-bottom:2px solid #4f98a3;letter-spacing:0.3px">Servizio</th><th style="background:#f5f5f3;padding:10px 12px;text-align:right;font-size:10.5pt;font-weight:600;color:#1a5f6f;border-bottom:2px solid #4f98a3;letter-spacing:0.3px">Prezzo</th></tr></thead><tbody><tr><td style="padding:10px 12px;border-bottom:1px solid #e8e8e6;color:#3a3a37">Reportage fotografico</td><td style="padding:10px 12px;border-bottom:1px solid #e8e8e6;color:#3a3a37;text-align:right">€ 1.500,00</td></tr><tr><td style="padding:10px 12px;border-bottom:1px solid #e8e8e6;color:#3a3a37">Album 30×30</td><td style="padding:10px 12px;border-bottom:1px solid #e8e8e6;color:#3a3a37;text-align:right">€ 1.000,00</td></tr></tbody><tfoot><tr><td style="padding:10px 12px;font-weight:600;background:#f5f5f3;color:#1a5f6f;border-bottom:2px solid #4f98a3">Totale</td><td style="padding:10px 12px;font-weight:600;background:#f5f5f3;color:#1a5f6f;border-bottom:2px solid #4f98a3;text-align:right">€ 2.500,00</td></tr></tfoot></table>',
};

// ── Diagnostics helpers ───────────────────────────────────────────────────────

export function getDiagnostics(
  sections: ContractSection[],
  customVars: CustomVariable[],
  overrides: Record<string, string>,
): DiagnosticCount {
  const customKeys = new Set(customVars.map(c => c.key));
  const allBody = sections
    .filter(s => s.isVisible)
    .map(s => overrides[s.id] ?? s.body)
    .join('\n');

  const tokens = [...allBody.matchAll(/\{\{([^}]+)\}\}/g)].map(m => m[1]);
  let placeholder = 0;
  let missing = 0;
  for (const key of tokens) {
    if (key in PLACEHOLDER_MAP || customKeys.has(key)) placeholder++;
    else missing++;
  }
  return { placeholder, missing };
}

// ── Markdown renderer (via marked — parity con backend) ──────────────────────

marked.use({ gfm: true, breaks: false, async: false });

function renderMarkdown(md: string): string {
  return marked.parse(md, { async: false }) as string;
}

// ── Build live preview HTML (multipagina) ────────────────────────────────────

export function buildLivePreviewHtml(
  sections: ContractSection[],
  customVars: CustomVariable[],
  contractTitle: string | null,
): string {
  const visibleSections = sections.filter(s => s.isVisible);

  const sectionsHtml = visibleSections.map((s) => {
    let body = s.body || '';
    const tokens: Record<string, string> = {};
    let idx = 0;

    // Step 1: sostituisci {{variabili}} con placeholder temporanei PRIMA di renderMarkdown
    for (const [key, val] of Object.entries(PLACEHOLDER_MAP)) {
      const placeholder = `\x00VAR${idx}\x00`;
      body = body.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), placeholder);
      tokens[placeholder] = `<span class="var-placeholder" title="{{${key}}}">${val}</span>`;
      idx++;
    }
    for (const cv of customVars) {
      const placeholder = `\x00VAR${idx}\x00`;
      const display = cv.defaultValue || `[${cv.label}]`;
      body = body.replace(new RegExp(`\\{\\{${cv.key}\\}\\}`, 'g'), placeholder);
      tokens[placeholder] = `<span class="var-custom" title="{{${cv.key}}}: ${cv.label}">${display}</span>`;
      idx++;
    }
    // variabili non risolte
    body = body.replace(/\{\{([^}]+)\}\}/g, (_m, k) => {
      const placeholder = `\x00VAR${idx++}\x00`;
      tokens[placeholder] = `<span class="var-missing" title="{{${k}}}">___</span>`;
      return placeholder;
    });

    // Step 2: renderMarkdown (fa escape dell'HTML, ma placeholder non sono toccati)
    let rendered = renderMarkdown(body);

    // Step 3: ripristina i placeholder con gli <span> reali
    for (const [ph, span] of Object.entries(tokens)) {
      rendered = rendered.replace(ph, span);
    }

    return `<div class="section"><h2>${s.title}</h2>${rendered}</div>`;
  }).join('\n');

  const displayTitle = contractTitle || 'CONTRATTO DI SERVIZI FOTOGRAFICI';

  let body = `<h1>${displayTitle}</h1>
<p class="subtitle">[Nome Studio]</p>
${sectionsHtml}
<div class="footer">Documento generato il [01/05/2025] · [Studio Nome]</div>`;

  return `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8"><style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{--page-width:210mm;--page-height:297mm;--page-px:794;--page-h-px:1123;--pad-x:56px;--pad-y:48px}
html{overflow-x:hidden}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Roboto','Helvetica Neue',sans-serif;font-size:11pt;color:#2c2c2c;line-height:1.7;counter-reset:page;-webkit-font-smoothing:antialiased}
.page{width:var(--page-px);min-height:var(--page-h-px);padding:var(--pad-y) var(--pad-x);background:#fafaf8;position:relative}
@media print{body{margin:0}.page{margin:0 auto!important;box-shadow:none;page-break-inside:avoid;background:#fff}}
h1{font-family:Georgia,'Garamond',serif;font-size:28pt;font-weight:400;text-align:center;margin:0 0 8px;color:#1a5f6f;line-height:1.2;word-break:break-word;overflow-wrap:break-word;letter-spacing:-0.3px}
.subtitle{text-align:center;font-size:10.5pt;color:#6b6b68;margin:0 0 32px;font-weight:500;letter-spacing:0.5px}
h2{font-family:Georgia,'Garamond',serif;font-size:14pt;font-weight:400;margin:32px 0 12px;color:#1a5f6f;border-bottom:2px solid #4f98a3;padding-bottom:8px;text-transform:none;word-break:break-word}
.section{margin-bottom:20px;page-break-inside:avoid}
p{margin-bottom:10px;line-height:1.7;color:#3a3a37}
strong{font-weight:600;color:#1a1a18}
em{font-style:italic;color:#5a5957}
ul,ol{margin:12px 0 12px 28px;color:#3a3a37}li{margin-bottom:6px;line-height:1.6}
table{width:100%;border-collapse:collapse;margin:20px 0;table-layout:auto;background:#fff;border-radius:6px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.05)}
th{background:#f5f5f3;padding:10px 12px;text-align:left;font-size:10.5pt;font-weight:600;color:#1a5f6f;border-bottom:2px solid #4f98a3;word-break:normal;letter-spacing:0.3px}
td{padding:10px 12px;border-bottom:1px solid #e8e8e6;font-size:11pt;word-break:break-word;overflow-wrap:break-word;color:#3a3a37}
tbody tr:last-child td{border-bottom:none}
.footer{margin-top:50px;font-size:9pt;color:#8a8985;text-align:center;border-top:1px solid #dcdcda;padding-top:20px;clear:both;letter-spacing:0.2px}
.var-placeholder{background:#e3f0ff;color:#1a5f6f;border-radius:4px;padding:2px 6px;font-style:italic;cursor:help;white-space:nowrap;border:0.5px solid #4f98a3}
.var-custom{background:#fff9e6;color:#e8af34;border-radius:4px;padding:2px 6px;font-style:italic;cursor:help;white-space:nowrap;border:0.5px solid #e8af34}
.var-missing{background:#ffe8e8;color:#a71c1c;border-radius:4px;padding:2px 6px;font-style:italic;text-decoration:underline dotted;cursor:help;white-space:nowrap;border:0.5px solid #a71c1c}
@media print{.var-placeholder,.var-custom,.var-missing{background:none!important;color:inherit!important;text-decoration:none!important;border:none!important;font-style:normal;padding:0;margin:0}}
</style></head><body><div class="page">${body}</div></body></html>`;
}
